import { enums, isStatusAfterStatus } from "@fraction/shared";

/**
 * The borrower/broker-facing loan lifecycle, collapsed to five legible stages
 * for the progress strip. This is intentionally coarser than the ~25-value
 * `ApplicationStatus` enum (and distinct from the 9-block `SIMPLE_STATUS_ORDER`
 * used by the employee funding progress) — borrowers don't need underwriting's
 * granularity, they need "where am I in my loan".
 *
 * Shared by the web dashboard (ApplicationProgressStrip, useFileLockState) and
 * the mobile app (StageStrip).
 */
export type ApplicationStageKey = "application" | "verification" | "underwriting" | "approved" | "closing";

interface StageDef {
  key: ApplicationStageKey;
  label: string;
  /**
   * First status (in canonical `UI_SYNTHETIC_STATUS_ORDER`) that puts the file
   * at-or-past this stage. Boundaries are listed in ascending order so the
   * current stage is simply the last boundary the file has reached.
   */
  boundary: enums.ApplicationStatus;
}

export const APPLICATION_STAGES: StageDef[] = [
  { key: "application", label: "Application", boundary: enums.ApplicationStatus.PARTIALLY_COMPLETED },
  { key: "verification", label: "Verification", boundary: enums.ApplicationStatus.MISSING_INFORMATION },
  // LENDER_MATCHING, not UNDERWRITING: it's the first status in canonical order that means
  // "assessment has started", and it works for both business lines. A Grid deal being
  // matched to lenders reads as Underwriting to the borrower, and a Fraction-as-lender deal
  // in UNDERWRITING still clears this boundary because UNDERWRITING sorts after it.
  { key: "underwriting", label: "Underwriting", boundary: enums.ApplicationStatus.LENDER_MATCHING },
  {
    key: "approved",
    label: "Approved",
    boundary: enums.ApplicationStatus.APPROVED_FOR_COMMITMENT_LETTER,
  },
  { key: "closing", label: "Closing", boundary: enums.ApplicationStatus.CONVEYANCER_ENGAGED },
];

// Files that didn't proceed — rendered as a terminal state rather than a point
// on the rail.
const TERMINAL_STATUSES = new Set<enums.ApplicationStatus>([
  enums.ApplicationStatus.REJECTED,
  enums.ApplicationStatus.HOMEOWNER_RESCINDED,
]);

// Off-rail statuses that sort after FUNDED in `UI_SYNTHETIC_STATUS_ORDER` but
// aren't past underwriting. A pending decline is still an open credit decision
// (the BDM is reaching out before we decline), so it must read as Underwriting
// and stay editable — never as approved, closing, or locked.
const UNDERWRITING_HOLD_STATUSES = new Set<enums.ApplicationStatus>([
  enums.ApplicationStatus.PENDING_DECLINE,
]);
const UNDERWRITING_STAGE_INDEX = APPLICATION_STAGES.findIndex((s) => s.key === "underwriting");

export interface ApplicationStageResult {
  /** 0..4 along the rail, or -1 when terminal. */
  index: number;
  key: ApplicationStageKey | "terminal";
  label: string;
  terminal: boolean;
}

export function getApplicationStage(status?: enums.ApplicationStatus): ApplicationStageResult {
  if (!status) {
    return { index: 0, key: "application", label: "Application", terminal: false };
  }
  if (TERMINAL_STATUSES.has(status)) {
    return {
      index: -1,
      key: "terminal",
      label: status === enums.ApplicationStatus.REJECTED ? "Not proceeding" : "Rescinded",
      terminal: true,
    };
  }
  if (UNDERWRITING_HOLD_STATUSES.has(status)) {
    const stage = APPLICATION_STAGES[UNDERWRITING_STAGE_INDEX];
    return { index: UNDERWRITING_STAGE_INDEX, key: stage.key, label: stage.label, terminal: false };
  }
  let index = 0;
  for (let i = 0; i < APPLICATION_STAGES.length; i++) {
    if (isStatusAfterStatus(status, APPLICATION_STAGES[i].boundary, true)) {
      index = i;
    }
  }
  const stage = APPLICATION_STAGES[index];
  return { index, key: stage.key, label: stage.label, terminal: false };
}

/**
 * The file is locked once underwriting has approved it: income/assets/debts and
 * property become read-only for borrowers and brokers (document upload stays
 * open). Mirrors the boundary used by {@link APPLICATION_STAGES} "approved",
 * including its pending-decline carve-out.
 */
export function isFileLocked(status?: enums.ApplicationStatus): boolean {
  return (
    !!status &&
    !UNDERWRITING_HOLD_STATUSES.has(status) &&
    isStatusAfterStatus(status, enums.ApplicationStatus.APPROVED_FOR_COMMITMENT_LETTER, true)
  );
}
